import type { NativeContextRNProps } from "../../shared/props/NativeContextRNProps";
import type { BuildVariant } from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/BuildVariant";
import type { Environment } from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/Environment";
import type { RNPlatformsV4 } from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/RNPlatformsV4";

import { getDeviceProperties } from "../../shared/logging/LoggableDeviceProperties";
import { useAceContext } from "../util/AceContext";

import { type ReactNode, createContext, useContext, useEffect, useMemo } from "react";

// TODO: replace with real session id when a repo-wide solution lands.
export const SESSIONID_UNAVAILABLE = "SESSIONID_UNAVAILABLE";

/** Shared envelope consumed by both ProductEvent and OperationalEvent payloads. */
export interface CareerScoutLoggingEnvelope {
  accountKey: string | null;
  country: string | null;
  lang: string | null;
  ctk: string | null;
  useragentraw: string | null;
  proctorGroupsString: string;
  platform: keyof typeof RNPlatformsV4;
  osVersion: string;
  nativeAppVersion: string;
  rnLibraryVersion: string;
  rnAppBundleVersion: string;
  variant?: { "com.indeed.signals.schema.reactnative.BuildVariant": BuildVariant } | null;
  env?: { "com.indeed.signals.schema.reactnative.Environment": Environment } | null;
  manufacturer?: { string: string } | null;
  model?: { string: string } | null;
  brand?: { string: string } | null;
  systemName?: { string: string } | null;
  /** Per-device unique id from the native bridge (RN-3941). ProductEvent-only field. */
  deviceId: string | null;
}

const CareerScoutLoggingReactContext = createContext<CareerScoutLoggingEnvelope | null>(null);

/** Reads the envelope from context. Throws if called outside the provider. */
export const useCareerScoutLoggingContext = (): CareerScoutLoggingEnvelope => {
  const envelope = useContext(CareerScoutLoggingReactContext);
  if (!envelope) {
    throw new Error(
      "useCareerScoutLoggingContext must be used within CareerScoutLoggingContextProvider",
    );
  }
  return envelope;
};

// Module-level singleton so operational log functions outside the React tree
// (error boundaries, async utilities, module init) can still read the envelope.
let envelopeSingleton: CareerScoutLoggingEnvelope | null = null;

/** Returns the envelope synced by the provider, or null before provider mount. */
export const getCareerScoutLoggingEnvelope = (): CareerScoutLoggingEnvelope | null =>
  envelopeSingleton;

export const __resetCareerScoutLoggingEnvelope = (): void => {
  envelopeSingleton = null;
};

const parseLocale = (
  locale: string | undefined,
): { lang: string | null; country: string | null } => {
  if (!locale) {
    return { lang: null, country: null };
  }
  const parts = locale.split(/[-_]/);
  return {
    lang: parts[0] || null,
    country: parts[1] || null,
  };
};

interface CareerScoutLoggingContextProviderProps {
  nativeContext?: NativeContextRNProps;
  /** Override account key. Falls back to `useAceContext().accountId` for callers
   *  inside the Ace tree. Pass explicitly when mounting outside Ace. */
  accountId?: string | null;
  /** Override proctor groups string. Falls back to `useAceContext().proctorGroups`. */
  proctorGroups?: string | null;
  /** Override ctk. Falls back to `nativeContext?.ctk`. */
  ctk?: string | null;
  children: ReactNode;
}

/**
 * Provides the shared Career Scout logging envelope to descendant components
 * and to the module-level operational log functions via the envelope singleton.
 */
export const CareerScoutLoggingContextProvider = ({
  nativeContext,
  accountId: accountIdProp,
  proctorGroups: proctorGroupsProp,
  ctk: ctkProp,
  children,
}: CareerScoutLoggingContextProviderProps) => {
  const aceContext = useAceContext();
  const accountId = accountIdProp !== undefined ? accountIdProp : aceContext.accountId;
  const proctorGroups =
    proctorGroupsProp !== undefined ? proctorGroupsProp : aceContext.proctorGroups;
  const ctk = ctkProp !== undefined ? ctkProp : (nativeContext?.ctk ?? null);

  const envelope: CareerScoutLoggingEnvelope = useMemo(() => {
    const deviceProperties = getDeviceProperties();
    const { lang, country } = parseLocale(nativeContext?.locale);
    return {
      accountKey: accountId ?? null,
      country,
      lang,
      ctk,
      useragentraw: null,
      proctorGroupsString: proctorGroups ?? "",
      platform: deviceProperties.platform,
      osVersion: deviceProperties.osVersion,
      nativeAppVersion: deviceProperties.nativeAppVersion,
      rnLibraryVersion: deviceProperties.rnLibraryVersion,
      rnAppBundleVersion: deviceProperties.rnAppBundleVersion,
      variant: deviceProperties.variant,
      env: deviceProperties.env,
      manufacturer: "manufacturer" in deviceProperties ? deviceProperties.manufacturer : null,
      model: "model" in deviceProperties ? deviceProperties.model : null,
      brand: "brand" in deviceProperties ? deviceProperties.brand : null,
      systemName: "systemName" in deviceProperties ? deviceProperties.systemName : null,
      deviceId: nativeContext?.deviceId ?? null,
    };
  }, [accountId, proctorGroups, nativeContext, ctk]);

  useEffect(() => {
    envelopeSingleton = envelope;
  }, [envelope]);

  return (
    <CareerScoutLoggingReactContext.Provider value={envelope}>
      {children}
    </CareerScoutLoggingReactContext.Provider>
  );
};
